<template>
    <div class="announcement-menu-container" ref="announcementMenu">

        <div class="announcement-icon" role="button" tabindex="0"
            :aria-label="badgeCount > 0 ? badgeCount + ' unread announcements' : 'Announcements'"
            aria-haspopup="true" :aria-expanded="showAnnouncements ? 'true' : 'false'"
            @click="toggleAnnouncements" @keydown.enter.prevent="toggleAnnouncements" @keydown.space.prevent="toggleAnnouncements">
           <svg width="34" height="34" viewBox="0 0 1.632 1.632" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><g/><path :style="{ fill: 'var(--primary-color)' }"  d="M1.26 0.299 0.617 0.459H0.24v0.077H0.087v0.357h0.153v0.077h0.092l0.069 0.357h0.187l-0.069 -0.357h0.097l0.64 0.16v-0.238h0.084V0.535h-0.086V0.298zm-1.02 0.543h-0.102V0.587h0.102zm0.288 0.433H0.444l-0.058 -0.306h0.084zm0.069 -0.357H0.288V0.51h0.306v0.408zm0.612 -0.383v0.528l-0.561 -0.14V0.505l0.56 -0.141zm0.082 0.051v0.255h-0.033V0.587zm0.109 0.103h0.122v0.051h-0.122zm-2.137 14.78 0.117 -0.052 0.021 0.047 -0.118 0.052zm3.207 28.601 0.021 -0.047 0.116 0.052 -0.021 0.047z"/></svg>
           <span v-if="!isGlobalCountLoaded && !showAnnouncements" class="badge-shimmer" aria-hidden="true"></span>
           <span v-else-if="badgeCount > 0 && !showAnnouncements" class="badge" aria-hidden="true">{{ badgeCount }}</span>
        </div>

        <div class="announcement-popup" v-if="showAnnouncements" role="region" aria-label="Announcements">
            <div class="announcement-header">
                <div class="announcement-header-main">
                    <div class="announcement-header-info">
                        <span class="menu-label"> Announcement </span>
                        <span v-if="!isGlobalCountLoaded" class="header-badge-shimmer"></span>
                        <span v-else-if="badgeCount > 0" class="badge">{{ badgeCount }}</span>
                    </div>
                    <button class="view-all-link" @click="viewAll">View all</button>
                </div>
                <div class="announcement-header-tabs" role="tablist" aria-label="Filter announcements">
                    <span class="tab" role="tab" tabindex="0" :aria-selected="filter === 'all' ? 'true' : 'false'" :class="{ active: filter === 'all' }" @click="filterList('all')" @keydown.enter.prevent="filterList('all')" @keydown.space.prevent="filterList('all')">All</span>
                    <span class="tab" role="tab" tabindex="0" :aria-selected="filter === 'unread' ? 'true' : 'false'" :class="{ active: filter === 'unread' }" @click="filterList('unread')" @keydown.enter.prevent="filterList('unread')" @keydown.space.prevent="filterList('unread')">Unread</span>
                    <span class="tab" role="tab" tabindex="0" :aria-selected="filter === 'global' ? 'true' : 'false'" :class="{ active: filter === 'global' }" @click="filterList('global')" @keydown.enter.prevent="filterList('global')" @keydown.space.prevent="filterList('global')">Global</span>
                    <span class="tab" role="tab" tabindex="0" :aria-selected="filter === 'course' ? 'true' : 'false'" :class="{ active: filter === 'course' }" @click="filterList('course')" @keydown.enter.prevent="filterList('course')" @keydown.space.prevent="filterList('course')">Course</span>
                </div>
            </div>

            <div class="announcements" v-if="loading">
                <div v-for="i in 4" :key="'skel-' + i" class="bell-skeleton-card">
                    <div class="skel-line" style="width: 60px; height: 14px;"></div>
                    <div class="skel-line" style="width: 65%; height: 12px; margin-top: 10px;"></div>
                    <div class="skel-line" style="width: 100%; height: 8px; margin-top: 8px;"></div>
                    <div class="skel-line" style="width: 80%; height: 8px; margin-top: 6px;"></div>
                </div>
            </div>

            <div class="announcements" v-else-if="announcements.length > 0">
                <div v-for="announcement in announcements" :key="announcement.id">
                    <AnnouncementCard :announcement="announcement" @open="openAnnouncement" />
                </div>
            </div>

            <div v-else class="no-announcements">
                <img src="@/assets/images/no-announcements.svg" class="no-announcements-icon" alt="" aria-hidden="true" />
                <p class="no-announcements-title">{{ filter === 'all' ? 'No announcements yet' : 'Nothing here' }}</p>
                <p class="no-announcements-text">You’re all caught up.</p>
            </div>
        </div>

        <AnnouncementDetailModal :announcement="selectedAnnouncement" :visible="isDetailVisible" @close="closeDetail" />
    </div>
</template>

<script>
import AnnouncementCard from './AnnouncementCard.vue';
import AnnouncementDetailModal from './AnnouncementDetailModal.vue';
import { mapActions, mapGetters } from 'vuex';
import { AnnouncementScope } from '../../constants/announcement';

export default {
    name: 'AnnouncementMenu',
    components: {
        AnnouncementCard,
        AnnouncementDetailModal,
    },
    data() {
        return {
            showAnnouncements: false,
            filter: 'unread',
            loading: false,
            announcements: [],
            pageSize: 10,
            selectedAnnouncement: null,
            isDetailVisible: false,
        };
    },
    watch: {
        showAnnouncements: {
            async handler(isOpen) {
                if (isOpen) {
                    await this.loadAnnouncements();
                    await this.fetchAnnouncementCount();
                }
            }
        },
        filter() {
            this.loadAnnouncements();
        },
    },
    computed: {
        ...mapGetters('announcement', ['unreadCount', 'isCountLoaded']),

        badgeCount() {
            return this.unreadCount();
        },
        isGlobalCountLoaded() {
            return this.isCountLoaded();
        },
    },
    methods: {
        ...mapActions('announcement', ['fetchAnnouncements', 'fetchAnnouncementCount', 'logAnnouncementView']),

        toggleAnnouncements() {
            this.showAnnouncements = !this.showAnnouncements;
        },
        filterList(filter) {
            this.filter = filter;
        },

        async loadAnnouncements() {
            this.loading = true;
            try {
                const result = await this.fetchAnnouncements(this.filterParams());
                this.announcements = result.payload || [];
            } catch (error) {
                this.announcements = [];
            } finally {
                this.loading = false;
            }
        },
        filterParams() {
            const params = { pageSize: this.pageSize, isNewestFirst: true };
            if (this.filter === 'unread') params.onlyUnread = true;
            else if (this.filter === 'global') params.onlyGlobal = true;
            else if (this.filter === 'course') params.entityType = AnnouncementScope.ProductVariant;
            return params;
        },
        viewAll() {
            this.showAnnouncements = false;
            this.$router.push('/user/announcements').catch(() => { });
        },

        async openAnnouncement(announcement) {
            this.showAnnouncements = false;
            this.selectedAnnouncement = announcement;
            this.isDetailVisible = true;
            if (announcement.isRead) return;

            await this.logAnnouncementView([announcement.id]);
            announcement.isRead = true;
            await this.fetchAnnouncementCount();
        },
        closeDetail() {
            this.isDetailVisible = false;
            this.selectedAnnouncement = null;
        },

        closeAnnouncements(event) {
            const menu = this.$refs.announcementMenu;
            if (!menu) return;
            if (!menu.contains(event.target)) {
                this.showAnnouncements = false;
            }
        },
    },
    created() {
        this.fetchAnnouncementCount();
    },
    mounted() {
        document.addEventListener("click", this.closeAnnouncements);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.closeAnnouncements);
    },
};
</script>

<style scoped>
.view-all-link {
    background: none;
    border: none;
    color: var(--primary-color);
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
}

.announcement-icon {
    position: relative;
    cursor: pointer;
    font-size: 15px;
}

.announcement-icon .badge {
    position: absolute;
    top: -7px;
    right: -7px;
    background: red;
    color: white;
    border-radius: 50%;
    padding: 3px 5px;
    font-size: 10px;
}

.badge-shimmer {
    position: absolute;
    top: -3px;
    right: -3px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #efefef;
    overflow: hidden;
}

.header-badge-shimmer {
    position: relative;
    display: inline-block;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    margin-left: 10px;
    background: #efefef;
    overflow: hidden;
}

.badge-shimmer::after,
.header-badge-shimmer::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.65) 50%, rgba(255, 255, 255, 0) 100%);
    transform: translateX(-100%);
    animation: bell-shimmer 1.6s infinite linear;
}

.announcement-popup {
    position: absolute;
    top: var(--header-height);
    width: 40%;
    right: 5%;
    background-color: white;
    border: 1px solid #ddd;
    z-index: 2;
}

.announcement-header {
    border-bottom: 1px solid #ddd;
    display: flex;
    flex-direction: column;
    font-weight: bold;
}

.announcement-header-main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid #ddd;
}

.announcement-header-main .announcement-header-info {
    display: flex;
    align-items: center;
}

.announcement-header-main .menu-label {
    color: #000;
    font-size: 1.4rem;
    font-weight: 500;
}

.announcement-header-main .badge {
    background: red;
    color: white;
    border-radius: 50%;
    padding: 3px 5px;
    font-size: 10px;
    margin-left: 10px;
}

.announcement-header-tabs {
    background: #F9F9F9;
    padding: 10px;
}

.announcement-header-tabs .tab {
    background: #F9F9F9;
    padding: 10px;
    cursor: pointer;
    margin-right: 5px;
    font-size: 0.8rem;
}

.announcement-header-tabs .tab.active {
    color: var(--primary-color);
}

.announcements {
    max-height: calc(100svh - var(--header-height) - 120px);
    overflow-y: auto;
}

.no-announcements {
    padding: 40px 20px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.no-announcements-icon {
    width: 38px;
    height: 38px;
    color: #cbd5e1;
    margin-bottom: 8px;
}

.no-announcements-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #374151;
    margin: 0;
}

.no-announcements-text {
    font-size: 0.82rem;
    color: #6b7280;
    margin: 0;
    font-weight: 400;
}

.bell-skeleton-card {
    border-bottom: 1px solid #f1f1f1;
    padding: 15px;
}

.skel-line {
    position: relative;
    display: block;
    background: #e2e2e2;
    border-radius: 4px;
    overflow: hidden;
}

.skel-line::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.65) 50%, rgba(255, 255, 255, 0) 100%);
    transform: translateX(-100%);
    animation: bell-shimmer 1.6s infinite linear;
}

@keyframes bell-shimmer {
    100% {
        transform: translateX(100%);
    }
}

@media (prefers-reduced-motion: reduce) {
    .skel-line::after {
        animation: none;
        opacity: 0;
    }
}

@media (max-width: 768px) {
    .announcement-popup {
        width: 80%;
        right: 10%;
    }
}

@media (max-width: 600px) {
    .announcement-popup {
        width: 100%;
        right: 0;
    }
}
</style>
